import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { getIdToken } from '../../getIdToken';
import IntegrationsService from '../../services/Integrations/IntegrationsService';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

/**
 * ZohoCallback component
 * Handles OAuth callback for both Zoho Books and Zoho Projects
 * 
 * Query Parameters:
 * - code: OAuth authorization code
 * - location: Zoho region (com, eu, in, com.cn, com.au)
 * - accounts-server: Zoho accounts server URL
 * - product: (optional) Product type (BOOKS, PROJECTS) - defaults to BOOKS if not specified
 */
const ZohoCallback = () => {
  const query = useQuery();
  const [urlToCall, setUrlToCall] = useState('');
  const [token, setToken] = useState('');
  const [result, setResult] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const integrationsService = new IntegrationsService();

  useEffect(() => {
    const code = query.get('code');
    const location = query.get('location');
    const accountsServer = query.get('accounts-server');
    // Detect product from URL path (projects/callback = PROJECTS, otherwise BOOKS)
    const product = window.location.pathname.includes('/projects/callback') ? 'PROJECTS' : (query.get('product') || 'BOOKS');

    if (!code) {
      setError('No code found in URL.');
      return;
    }

    const callBackend = async () => {
      try {
        const tokenVal = await getIdToken();
        setToken(tokenVal);

        // Construct the callback URL based on product type
        let callbackEndpoint = '/integrations/zoho/callback';
        if (product === 'PROJECTS') {
          callbackEndpoint = '/integrations/zoho/projects/callback';
        }

        const url = `${process.env.REACT_APP_INTEGRATIONS_API_URL}${callbackEndpoint}?code=${encodeURIComponent(code)}&location=${encodeURIComponent(location)}&accounts-server=${encodeURIComponent(accountsServer)}&ts=${Date.now()}`;
        
        setUrlToCall(url);

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${tokenVal}`,
            'Content-Type': 'application/json',
          },
        });

        const text = await response.text();

        if (response.status === 200) {
          // Parse response if it's JSON
          let responseData;
          try {
            responseData = JSON.parse(text);
          } catch (e) {
            responseData = { message: text };
          }

          console.log('Callback successful:', responseData);

          // For Books integration, optionally fetch additional metadata
          if (product === 'BOOKS') {
            try {
              const orgResp = await integrationsService.zohoGetOrganizations();
              console.log('Fetched organization metadata:', orgResp);
            } catch (orgErr) {
              console.error('Failed to fetch organization metadata:', orgErr);
              // Don't fail the whole flow if this fails
            }
          }

          // Redirect back to integrations page after successful callback
          navigate('/integrations', { replace: true });
        } else {
          setResult(`Status: ${response.status} ${response.statusText}\nBody: ${text}`);
          setError('Failed to process Zoho callback');
        }
      } catch (err) {
        console.error('Callback error:', err);
        setError(err.message || 'Unknown error');
      }
    };

    callBackend();
    // eslint-disable-next-line
  }, []);

  // Show error if something went wrong
  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Error Processing Zoho Integration</h2>
        <p style={{ color: 'red' }}>{error}</p>
        <button onClick={() => navigate('/integrations')}>
          Back to Integrations
        </button>
      </div>
    );
  }

  // Show processing message
  return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <h2>Processing Zoho Integration...</h2>
      <p>Please wait while we complete your integration setup.</p>
    </div>
  );
};

export default ZohoCallback;

